/**
 * TanStack hooks for the enrichment control rail — failure-point discovery (#1),
 * Temporal rewind (#2), and cooperative pause/resume (#6/#7). All key on the
 * root workflow id the adapter exposes via `workflowControls.workflowId`.
 *
 * Controls act on the *live* Temporal workflow, so a `404` (run closed between
 * render and click) and a `501` (flow can't pause) are expected races, not
 * faults — they toast as info, not error, mirroring the workflow-tracking console.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getEnrichmentFailurePoints,
  getEnrichmentRunHealth,
  getEnrichmentRunProgress,
  pauseEnrichmentWorkflow,
  restartEnrichmentWorkflow,
  resumeEnrichmentWorkflow,
  rewindEnrichmentWorkflow,
} from '../services/workflow-control.service';
import type { EnrichmentConfigOverride, WorkflowRetryMode } from '../types/workflow-control.types';

/** Operator identity attached to every control request as `requestedBy`. */
function useRequestedBy(): { requestedBy: string } | Record<string, never> {
  const email = useAuthStore((s) => s.user?.email);
  return email ? { requestedBy: email } : {};
}

/**
 * Shared control-failure handler. `404` → the run closed (benign race); `501` →
 * the flow doesn't support pause/resume; `409` → the state we rendered was stale.
 * Everything else is a real error.
 */
function useControlError() {
  const { t } = useTranslation('content-enrichment');
  return (err: unknown) => {
    const status = err instanceof ApiError ? err.statusCode : null;
    if (status === 404) {
      toast.info(t('controls.toast.alreadyClosed'));
      return;
    }
    if (status === 501) {
      toast.info(t('controls.toast.unsupported'));
      return;
    }
    if (status === 400) {
      toast.warning(extractApiError(err, t('controls.toast.invalidConfig')));
      return;
    }
    if (status === 409) {
      toast.warning(extractApiError(err, t('controls.toast.stale')));
      return;
    }
    toast.error(extractApiError(err, t('controls.toast.error')));
  };
}

/** #1 — discover retryable failure points for a failed pipeline run. Gated to
 *  failed runs (`enabled`) so healthy runs don't spend the request. */
export function useEnrichmentFailurePoints(workflowId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.contentEnrichment.failurePoints(workflowId ?? ''),
    queryFn: () => getEnrichmentFailurePoints(workflowId ?? ''),
    enabled: enabled && !!workflowId,
  });
}

/** #6 — pause the run at the next stage boundary. `onSettled` lets the rail
 *  refetch the status it gates on once the request lands. */
export function usePauseEnrichment(workflowId: string, onSettled?: () => void) {
  const { t } = useTranslation('content-enrichment');
  const requestedBy = useRequestedBy();
  const onError = useControlError();
  return useMutation({
    mutationFn: () => pauseEnrichmentWorkflow(workflowId, requestedBy),
    onSuccess: () => toast.success(t('controls.toast.paused')),
    onError,
    onSettled: () => onSettled?.(),
  });
}

/** #7 — resume a paused run. */
export function useResumeEnrichment(workflowId: string, onSettled?: () => void) {
  const { t } = useTranslation('content-enrichment');
  const requestedBy = useRequestedBy();
  const onError = useControlError();
  return useMutation({
    mutationFn: () => resumeEnrichmentWorkflow(workflowId, requestedBy),
    onSuccess: () => toast.success(t('controls.toast.resumed')),
    onError,
    onSettled: () => onSettled?.(),
  });
}

/** #2 — rewind (`mode: 'RESET'`) or resume-remaining (`mode: 'RESUME_REMAINING'`)
 *  the run from the operator-chosen failure point (`eventId`). `runId` is the
 *  failure-points response's inspected run. A `400` means the chosen point/mode
 *  is no longer valid (the page is stale) — refetch failure-points so the dialog
 *  re-renders with the server's current `retryModes` instead of the shared
 *  control-error toast. */
export function useRewindEnrichment(workflowId: string, onSettled?: () => void) {
  const { t } = useTranslation('content-enrichment');
  const email = useAuthStore((s) => s.user?.email);
  const queryClient = useQueryClient();
  const onError = useControlError();
  return useMutation({
    mutationFn: ({
      eventId,
      runId,
      mode,
    }: {
      eventId: number;
      runId: string;
      mode: WorkflowRetryMode;
    }) =>
      rewindEnrichmentWorkflow(
        workflowId,
        eventId,
        mode === 'RESUME_REMAINING'
          ? `Continue remaining products — triggered from content-enrichment UI by ${email ?? 'unknown user'}`
          : `Re-run enrichment from start — triggered from content-enrichment UI by ${email ?? 'unknown user'}`,
        mode,
        runId,
      ),
    onSuccess: (data) =>
      toast.success(
        data.mode === 'RESUME_REMAINING'
          ? t('controls.toast.retriedResume')
          : t('controls.toast.retried'),
      ),
    onError: (err) => {
      if (err instanceof ApiError && err.statusCode === 400) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.contentEnrichment.failurePoints(workflowId),
        });
        toast.warning(extractApiError(err, t('controls.toast.retryStale')));
        return;
      }
      onError(err);
    },
    onSettled: () => onSettled?.(),
  });
}

/** Restart gate — polls run detail to learn whether the run is at ENRICHMENT or
 *  later and how many curated products remain. Enabled only for flows that
 *  support restart; refetched while the run is live so the gate stays fresh. */
export function useEnrichmentRunProgress(
  workflowId: string | null,
  enabled: boolean,
  isRunning: boolean,
) {
  return useQuery({
    queryKey: queryKeys.contentEnrichment.runProgress(workflowId ?? ''),
    queryFn: () => getEnrichmentRunProgress(workflowId ?? ''),
    enabled: enabled && !!workflowId,
    refetchInterval: isRunning ? 15_000 : false,
  });
}

/** Live paused/health poll for the pipeline run. The funnel's trigger-status
 *  poll doesn't carry `paused`, so read it from the run-detail endpoint while the
 *  run is live. `retry: false` — a `404`/`501` (run closed, or a flow that can't
 *  pause) is a benign "not paused", not an error to retry. */
export function useEnrichmentRunHealth(workflowId: string | null, isLive: boolean) {
  return useQuery({
    queryKey: queryKeys.contentEnrichment.runHealth(workflowId ?? ''),
    queryFn: () => getEnrichmentRunHealth(workflowId ?? ''),
    enabled: isLive && !!workflowId,
    refetchInterval: isLive ? 10_000 : false,
    retry: false,
  });
}

/** Hard-restart the run under the same id on its remaining products, optionally
 *  raising parallelism. The fresh parent reuses the workflow id, so `onSettled`
 *  just refetches the status the funnel already polls. The caller owns the
 *  success toast (it knows the remaining count); errors route through the shared
 *  control-error handler. */
export function useRestartEnrichment(workflowId: string, onSettled?: () => void) {
  const email = useAuthStore((s) => s.user?.email);
  const onError = useControlError();
  return useMutation({
    mutationFn: (config?: EnrichmentConfigOverride) =>
      restartEnrichmentWorkflow(workflowId, {
        ...(config ? { config } : {}),
        ...(email ? { requestedBy: email } : {}),
        reason: `Restarted from content-enrichment UI by ${email ?? 'unknown user'}`,
      }),
    onError,
    onSettled: () => onSettled?.(),
  });
}
